<template>
  <div class="container-fluid p-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center mb-3">
          <button class="btn btn-outline-secondary me-3" @click="goBack">
            <i class="bi bi-arrow-left me-2"></i>Back
          </button>
          <h2 class="mb-0 fw-bold gradient-text">Settings</h2>
        </div>
      </div>
    </div>

    <!-- Settings Content -->
    <div class="row">
      <div class="col-lg-8 mx-auto">
        
        <!-- General Settings -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">
            <i class="bi bi-gear me-2"></i>General Settings
          </h5>
          
          <!-- Language -->
          <div class="mb-4">
            <label class="form-label">Language</label>
            <select class="form-select" v-model="settings.language">
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </div>

          <!-- Timezone -->
          <div class="mb-4">
            <label class="form-label">Timezone</label>
            <select class="form-select" v-model="settings.timezone">
              <option value="UTC">UTC</option>
              <option value="EST">Eastern Standard Time</option>
              <option value="PST">Pacific Standard Time</option>
              <option value="CST">Central Standard Time</option>
              <option value="MST">Mountain Standard Time</option>
            </select>
          </div>

          <!-- Auto-save -->
          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="settings.autoSave"
              id="autoSave"
            >
            <label class="form-check-label" for="autoSave">
              Auto-save progress
            </label>
          </div>
        </div>

        <!-- Notification Settings -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">
            <i class="bi bi-bell me-2"></i>Notifications
          </h5>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="notifications.email"
              id="emailNotif"
            >
            <label class="form-check-label" for="emailNotif">
              Email Notifications
            </label>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="notifications.taskReminders"
              id="taskReminders"
            >
            <label class="form-check-label" for="taskReminders">
              Task Reminders
            </label>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="notifications.progressUpdates"
              id="progressUpdates"
            >
            <label class="form-check-label" for="progressUpdates">
              Progress Updates
            </label>
          </div>

          <div class="form-check mb-3" v-if="currentUser?.role === 'parent'">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="notifications.childProgress"
              id="childProgress"
            >
            <label class="form-check-label" for="childProgress">
              Child Progress Alerts
            </label>
          </div>

          <div class="form-check mb-3" v-if="currentUser?.role === 'teacher'">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="notifications.studentSubmissions"
              id="studentSubmissions"
            >
            <label class="form-check-label" for="studentSubmissions">
              Student Submission Alerts
            </label>
          </div>
        </div>

        <!-- Privacy Settings -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">
            <i class="bi bi-shield-lock me-2"></i>Privacy & Security
          </h5>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="privacy.shareProgress"
              id="shareProgress"
            >
            <label class="form-check-label" for="shareProgress">
              Share progress with teachers/parents
            </label>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="privacy.publicProfile"
              id="publicProfile"
            >
            <label class="form-check-label" for="publicProfile">
              Public profile visibility
            </label>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="privacy.dataAnalytics"
              id="dataAnalytics"
            >
            <label class="form-check-label" for="dataAnalytics">
              Allow data analytics for improvement
            </label>
          </div>
        </div>

        <!-- Learning Preferences -->
        <div class="modern-card p-4 mb-4" v-if="currentUser?.role === 'student'">
          <h5 class="fw-bold mb-4">
            <i class="bi bi-book me-2"></i>Learning Preferences
          </h5>

          <div class="mb-4">
            <label class="form-label">Difficulty Level</label>
            <select class="form-select" v-model="learning.difficultyLevel">
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div class="mb-4">
            <label class="form-label">Daily Goal (minutes)</label>
            <input 
              type="number" 
              class="form-control" 
              v-model="learning.dailyGoal"
              min="15"
              max="180"
            >
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="learning.soundEffects"
              id="soundEffects"
            >
            <label class="form-check-label" for="soundEffects">
              Enable sound effects
            </label>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              v-model="learning.animations"
              id="animations"
            >
            <label class="form-check-label" for="animations">
              Enable animations
            </label>
          </div>
        </div>

        <!-- Data Management -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">
            <i class="bi bi-database me-2"></i>Data Management
          </h5>

          <div class="d-flex gap-2 mb-3 align-items-center flex-wrap">
            <div class="d-flex align-items-center gap-2">
              <label class="form-label mb-0 me-2">Export format</label>
              <select class="form-select form-select-sm" style="width: auto" v-model="exportFormat">
                <option value="json">JSON (single file)</option>
                <option value="csv">CSV (ZIP of multiple files)</option>
              </select>
            </div>
            <button class="btn btn-outline-primary" @click="exportData">
              <i class="bi bi-download me-2"></i>Export Data
            </button>
            <button class="btn btn-outline-warning" @click="clearCache">
              <i class="bi bi-trash me-2"></i>Clear Cache
            </button>
          </div>

          <small class="text-muted">
            Export your data or clear cached information to free up space.
          </small>
        </div>

        <!-- Save Button -->
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary" @click="resetSettings">
            <i class="bi bi-arrow-clockwise me-2"></i>Reset to Defaults
          </button>
          <button class="btn btn-primary" @click="saveSettings">
            <i class="bi bi-check-lg me-2"></i>Save Settings
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppSettings',
  data() {
    return {
      currentUser: null,
      exportFormat: 'json',
      settings: {
        language: 'en',
        timezone: 'UTC',
        autoSave: true
      },
      notifications: {
        email: true,
        taskReminders: true,
        progressUpdates: true,
        childProgress: true,
        studentSubmissions: true
      },
      privacy: {
        shareProgress: true,
        publicProfile: false,
        dataAnalytics: true
      },
      learning: {
        difficultyLevel: 'intermediate',
        dailyGoal: 30,
        soundEffects: true,
        animations: true
      }
    }
  },
  mounted() {
    this.loadUserData()
    this.loadSettings()
  },
  methods: {
    loadUserData() {
      const storedUser = localStorage.getItem('currentUser')
      if (storedUser) {
        this.currentUser = JSON.parse(storedUser)
      }
    },

    async loadSettings() {
      try {
        const { getSettings } = await import('../services/userService.js')
        const saved = await getSettings()
        if (saved) {
          this.settings = { ...this.settings, ...(saved.settings || {}) }
          this.notifications = { ...this.notifications, ...(saved.notifications || {}) }
          this.privacy = { ...this.privacy, ...(saved.privacy || {}) }
          this.learning = { ...this.learning, ...(saved.learning || {}) }
        }
      } catch (e) {
        // Fallback to local storage if server not available
        const storedSettings = localStorage.getItem('userSettings')
        if (storedSettings) {
          const parsed = JSON.parse(storedSettings)
          this.settings = { ...this.settings, ...parsed.settings }
          this.notifications = { ...this.notifications, ...parsed.notifications }
          this.privacy = { ...this.privacy, ...parsed.privacy }
          this.learning = { ...this.learning, ...parsed.learning }
        }
      }
    },

    async saveSettings() {
      const allSettings = {
        settings: this.settings,
        notifications: this.notifications,
        privacy: this.privacy,
        learning: this.learning
      }
      try {
        const { updateSettings } = await import('../services/userService.js')
        await updateSettings(allSettings)
      } catch (e) {
        // Also persist locally as a fallback cache
        localStorage.setItem('userSettings', JSON.stringify(allSettings))
      }
      this.showMessage('Settings saved successfully!')
    },

    resetSettings() {
      if (confirm('Are you sure you want to reset all settings to defaults?')) {
        localStorage.removeItem('userSettings')
        this.loadSettings()
        this.showMessage('Settings reset to defaults')
      }
    },

    async exportData() {
      try {
        const base = process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'
        const token = localStorage.getItem('authToken')
        const requestUrl = new URL(`${base}/api/users/export`)
        if (this.exportFormat === 'csv') requestUrl.searchParams.set('format', 'csv')
        const res = await fetch(requestUrl.toString(), {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            'Authentication-Token': token || undefined
          }
        })
        if (!res.ok) throw new Error('Export failed')
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        const ext = this.exportFormat === 'csv' ? 'zip' : 'json'
        link.download = `lifeskill-data-${new Date().toISOString().split('T')[0]}.${ext}`
        link.click()
        URL.revokeObjectURL(blobUrl)
        this.showMessage(`Data exported successfully as ${this.exportFormat.toUpperCase()}`)
      } catch (e) {
        this.showMessage('Export failed')
      }
    },

    async clearCache() {
      if (!confirm('Are you sure you want to clear all cached data? This will not delete your account.')) return
      try {
        const base = process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'
        const token = localStorage.getItem('authToken')
        await fetch(`${base}/api/users/clear-cache`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            'Authentication-Token': token || undefined,
            'Content-Type': 'application/json'
          }
        })
      } catch (e) {
        // ignore server failures; still clear client cache
      }
      // Clear specific cache items but keep essential ones
      const keysToKeep = ['currentUser', 'userSettings']
      const allKeys = Object.keys(localStorage)
      allKeys.forEach(key => { if (!keysToKeep.includes(key)) localStorage.removeItem(key) })
      this.showMessage('Cache cleared successfully!')
    },

    goBack() {
      this.$router.go(-1)
    },

    showMessage(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.form-control:focus,
.form-select:focus {
  border-color: var(--role-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--role-primary), 0.25);
}

.btn-primary {
  background: var(--role-gradient);
  border: none;
}

.btn-primary:hover {
  background: var(--role-primary);
  transform: translateY(-1px);
}

.btn-outline-primary {
  border-color: var(--role-primary);
  color: var(--role-primary);
}

.btn-outline-primary:hover {
  background-color: var(--role-primary);
  border-color: var(--role-primary);
}

.form-check-input:checked {
  background-color: var(--role-primary);
  border-color: var(--role-primary);
}
</style> 